import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { FeatureDiscoveryState } from '../types';

type DiscoveryStep = keyof FeatureDiscoveryState;
type ViewType = 'home' | 'chat' | 'decisions' | 'insights' | 'journal';

interface StepConfig {
  id: DiscoveryStep;
  view: ViewType;
  requiresAction: boolean;
  nextStep: DiscoveryStep | null;
  navigateTo: ViewType | null;
}

const STEP_CONFIGS: StepConfig[] = [
  { id: 'pulseIntro', view: 'home', requiresAction: false, nextStep: 'homeInputDiscovered', navigateTo: null },
  { id: 'homeInputDiscovered', view: 'home', requiresAction: true, nextStep: 'voiceInputDiscovered', navigateTo: null },
  { id: 'voiceInputDiscovered', view: 'home', requiresAction: true, nextStep: 'photoInputDiscovered', navigateTo: null },
  { id: 'photoInputDiscovered', view: 'home', requiresAction: true, nextStep: 'journalDiscovered', navigateTo: 'journal' },
  { id: 'journalDiscovered', view: 'journal', requiresAction: false, nextStep: 'chatDiscovered', navigateTo: 'chat' },
  { id: 'chatDiscovered', view: 'chat', requiresAction: false, nextStep: 'decisionsDiscovered', navigateTo: 'decisions' },
  { id: 'decisionsDiscovered', view: 'decisions', requiresAction: false, nextStep: 'valuesDiscovered', navigateTo: 'insights' },
  { id: 'valuesDiscovered', view: 'insights', requiresAction: true, nextStep: 'goalsDiscovered', navigateTo: null },
  { id: 'goalsDiscovered', view: 'insights', requiresAction: true, nextStep: 'insightsDiscovered', navigateTo: null },
  { id: 'insightsDiscovered', view: 'insights', requiresAction: false, nextStep: null, navigateTo: null },
];

const STEP_IDS = STEP_CONFIGS.map(s => s.id);
const STEP_MAP = new Map(STEP_CONFIGS.map(s => [s.id, s]));

interface FeatureDiscoveryContextValue {
  state: FeatureDiscoveryState;
  currentStep: DiscoveryStep | null;
  completeStep: (step: DiscoveryStep) => void;
  getNextStepForView: (view: ViewType) => DiscoveryStep | null;
  getStepConfig: (step: DiscoveryStep) => StepConfig | undefined;
  isAllComplete: boolean;
  completedCount: number;
  totalSteps: number;
}

const FeatureDiscoveryContext = createContext<FeatureDiscoveryContextValue | null>(null);

interface FeatureDiscoveryProviderProps {
  children: React.ReactNode;
  discoveryState: FeatureDiscoveryState;
  onUpdateDiscovery: (step: DiscoveryStep) => void;
}

export const FeatureDiscoveryProvider: React.FC<FeatureDiscoveryProviderProps> = ({
  children,
  discoveryState,
  onUpdateDiscovery
}) => {
  const state = discoveryState || {};

  const completedCount = useMemo(() => {
    return STEP_IDS.filter(step => state[step] === true).length;
  }, [state]);

  const totalSteps = STEP_IDS.length;
  const isAllComplete = completedCount >= totalSteps;

  const currentStep = useMemo(() => {
    for (const step of STEP_IDS) {
      if (!state[step]) return step;
    }
    return null;
  }, [state]);

  const completeStep = useCallback((step: DiscoveryStep) => {
    if (!state[step]) {
      onUpdateDiscovery(step);
    }
  }, [state, onUpdateDiscovery]);

  const getNextStepForView = useCallback((view: ViewType): DiscoveryStep | null => {
    for (const step of STEP_IDS) {
      if (!state[step]) {
        const config = STEP_MAP.get(step);
        if (config?.view === view) {
          return step;
        }
        return null;
      }
    }
    return null;
  }, [state]);

  const getStepConfig = useCallback((step: DiscoveryStep): StepConfig | undefined => {
    return STEP_MAP.get(step);
  }, []);

  const value = useMemo(() => ({
    state,
    currentStep,
    completeStep,
    getNextStepForView,
    getStepConfig,
    isAllComplete,
    completedCount,
    totalSteps
  }), [state, currentStep, completeStep, getNextStepForView, getStepConfig, isAllComplete, completedCount, totalSteps]);

  return (
    <FeatureDiscoveryContext.Provider value={value}>
      {children}
    </FeatureDiscoveryContext.Provider>
  );
};

export const useFeatureDiscovery = () => {
  const context = useContext(FeatureDiscoveryContext);
  if (!context) {
    return {
      state: {},
      currentStep: null,
      completeStep: () => {},
      getNextStepForView: () => null,
      getStepConfig: () => undefined,
      isAllComplete: true,
      completedCount: 0,
      totalSteps: 0
    };
  }
  return context;
};

export { STEP_CONFIGS, STEP_MAP };
export type { StepConfig, ViewType };
export default FeatureDiscoveryContext;
